/**
 * Module dependencies.
 */

import { Balloon, balloons } from 'src/core/advisor-config/balloons';
import { GetStaticProps, NextPage } from 'next/types';
import {
  IconButton,
  RouterLink,
  Stepper,
  Svg,
  icons,
  logos
} from 'src/components/core';

import { Market, advisorMarketTypes } from 'src/types/markets';
import { color, media, units, useBreakpoint } from '@untile/react-components';
import { fetchMarkets } from 'src/api/markets/fetch-markets';
import { fetchPageFields } from 'src/api/page-fields/fetch-page-fields';
import {
  stepsKeys,
  stepsWithOtherOption,
  stepsWithRadios
} from 'src/core/advisor-config/steps-options';

import { theme } from 'styled-tools';
import { useAdvisor } from 'src/context/advisor/context';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import AdvisorProvider from 'src/context/advisor/provider';
import BalloonsStep from 'src/components/advisor/steps/balloons';
import Container from 'src/components/core/layout/container';
import GasContentSliderStep from 'src/components/advisor/steps/gas-content-slider';
import MarketsStep from 'src/components/advisor/steps/markets';
import Metatags from 'src/components/core/metatags';
import MobileSidebar from 'src/components/advisor/sidebar/mobile-sidebar';
import MockedBalloonsResultsStep from 'src/components/advisor/steps/mocked-balloons-results';
import MockedBalloonsStep from 'src/components/advisor/steps/mocked-balloons';
import ResultsStep from 'src/components/advisor/steps/results';
import Sidebar from 'src/components/advisor/sidebar';
import StepOtherOption from 'src/components/advisor/steps/step-other-option';
import StepRadioOptions from 'src/components/advisor/steps/step-radio-options';
import WaterCapacityRadioOptionsStep from 'src/components/advisor/steps/water-capacity-radio-options';
import WaterCapacitySliderStep from 'src/components/advisor/steps/water-capacity-slider';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  hasDoubleLogo?: boolean;
  markets: Market[] | null;
};

/**
 * `Wrapper` styled page.
 */

const Wrapper = styled.section`
  background-color: ${color('backgrounds.sage01')};
  display: flex;
  min-height: 100vh;
  position: relative;
  width: 100vw;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'header'
    'sidebar'
    'stepper';
  grid-template-rows: ${theme('dimensions.navbarHeightMobile')}px max-content 1fr;
  height: 100%;
  overflow: auto;

  ${media.min('md')`
    grid-template-areas:
      'header  header header'
      'sidebar .      stepper';
    grid-template-columns: 4fr ${theme('grid.columnsGutter')}px 8fr;
    grid-template-rows: ${theme('dimensions.navbarHeight')}px 1fr;
    min-height: 100vh;
  `}
`;

/**
 * `Header` styled component.
 */

const Header = styled.div`
  align-items: center;
  display: flex;
  grid-area: header;
  justify-content: space-between;

  ${media.min('md')`
    padding: 0 ${units(5)};
  `}
`;

/**
 * `LogoLink` styled component.
 */

const LogoLink = styled(RouterLink)`
  grid-area: logo;
  height: ${units(6)};
  transition: opacity ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * `CloseButton` styled component.
 */

const CloseButton = styled(IconButton).attrs({
  icon: icons.close,
  iconSize: units(3)
})`
  grid-area: closeButton;
  justify-self: end;
  line-height: 0;
  padding: 0;

  ${media.min('md')`
    align-self: center;
  `}
`;

/**
 * `SidebarWrapper` styled component.
 */

const SidebarWrapper = styled.div`
  display: flex;
  grid-area: sidebar;
`;

/**
 * `StyledStepper` styled component.
 */

const StyledStepper = styled(Stepper)`
  display: flex;
  grid-area: stepper;
  overflow: scroll;
  padding-top: ${units(9.5)};

  > div {
    width: 100%;
  }

  ${media.max('md')`
    padding-top: ${units(5)};
  `}
`;

/**
 * Advisor page content.
 */

const AdvisorContent: NextPage<Props> = props => {
  const { locale, translate } = useTranslate();
  const router = useRouter();
  const title = translate('advisor:title');
  const isMobile = useBreakpoint('md', 'max');
  const routeResolver = useLocalizedRoute();
  const { logo, logoSize } = useMemo(() => {
    if (isMobile) {
      return {
        logo: props?.hasDoubleLogo
          ? logos.doubleBrandLogoMobile
          : logos.amtrolAlfaLogoMobile,
        logoSize: props?.hasDoubleLogo ? '134px' : '48px'
      };
    }

    return {
      logo: props?.hasDoubleLogo ? logos.doubleBrandLogo : logos.amtrolAlfaLogo,
      logoSize: props?.hasDoubleLogo ? '509px' : '240px'
    };
  }, [props?.hasDoubleLogo, isMobile]);

  const { choices, currentStep, isSuccessModelRanges, lastAction } =
    useAdvisor();

  const currentStepKey = useMemo(() => {
    return !currentStep ? null : currentStep.nextStep.step.pageKey;
  }, [currentStep]);

  const [balloonSize, setBalloonSize] = useState<number>();
  const filteredBalloons = useMemo<Balloon[]>(() => {
    return filter(balloons, ({ size }) => size === balloonSize);
  }, [balloonSize]);

  return (
    <>
      <Metatags
        pageTitle={title}
        title={title}
      />

      <Wrapper>
        <Container>
          <Grid>
            <Header>
              <LogoLink
                href={routeResolver('home')}
                locale={locale}
              >
                <Svg
                  icon={logo}
                  size={logoSize}
                />
              </LogoLink>

              <CloseButton onClick={() => router.back()} />
            </Header>

            <SidebarWrapper>
              {isMobile ? (
                <MobileSidebar hasChoices={!isEmpty(choices)} />
              ) : (
                <Sidebar />
              )}
            </SidebarWrapper>

            <StyledStepper
              lastAction={lastAction}
              step={currentStepKey}
            >
              {!currentStepKey && <MarketsStep markets={props.markets} />}

              {includes(stepsWithRadios, currentStepKey) && (
                <StepRadioOptions
                  ignoreFallback={
                    choices &&
                    choices[0].value === advisorMarketTypes.helium &&
                    currentStepKey === stepsKeys.refillable
                  }
                  {...currentStep.nextStep.options}
                  {...currentStep.nextStep.step}
                />
              )}

              {includes(stepsWithOtherOption, currentStepKey) && (
                <StepOtherOption
                  {...currentStep.nextStep.options}
                  {...currentStep.nextStep.step}
                />
              )}

              {isSuccessModelRanges &&
                currentStepKey === stepsKeys.waterCapacitySlider && (
                  <WaterCapacitySliderStep {...currentStep.nextStep.step} />
                )}

              {isSuccessModelRanges &&
                currentStepKey === stepsKeys.gasContentSlider && (
                  <GasContentSliderStep {...currentStep.nextStep.step} />
                )}

              {currentStepKey === stepsKeys.waterCapacity && (
                <WaterCapacityRadioOptionsStep {...currentStep.nextStep.step} />
              )}

              {currentStepKey === stepsKeys.mockedBalloons && (
                <MockedBalloonsStep
                  balloonSize={balloonSize}
                  onSetBalloonSize={setBalloonSize}
                  {...currentStep.nextStep.step}
                />
              )}

              {currentStepKey === stepsKeys.mockedBalloonsResults && (
                <MockedBalloonsResultsStep
                  balloons={filteredBalloons}
                  {...currentStep.nextStep.step}
                />
              )}

              {currentStepKey === stepsKeys.balloons && (
                <BalloonsStep {...currentStep.nextStep.step} />
              )}

              {currentStepKey === stepsKeys.results && (
                <ResultsStep {...currentStep.nextStep.step} />
              )}
            </StyledStepper>
          </Grid>
        </Container>
      </Wrapper>
    </>
  );
};

/**
 * `Advisor` page.
 */

const Advisor: NextPage<Props> = props => (
  <AdvisorProvider>
    <AdvisorContent {...props} />
  </AdvisorProvider>
);

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => {
  const pageFields = await fetchPageFields({ locale });
  const markets = await fetchMarkets({ locale });

  if (!pageFields || !markets) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      hasDoubleLogo: pageFields?.homepageSecondLogo,
      hasFooter: false,
      hasNavbar: false,
      markets
    },
    revalidate: 60 * 60 * 24
  };
};

/**
 * Export `Advisor` page.
 */

export default Advisor;
